// Order/item pricing math and product resolution — the core "how much does
// this order cost/earn" logic. Extracted from public/index.html's inline
// script (Phase 2 modularization, slice 2).
//
// Unlike the utils/ modules (slice 1), these functions originally closed
// over the app's mutable `PRODUCTS`/`ORDERS` globals directly. Here they
// take `products`/`orders` as an explicit first argument instead (proper
// dependency injection) — that's what makes them independently testable
// (see tests/unit/pricing.test.mjs) without needing the whole app's global
// state. public/index.html keeps a one-line wrapper of the same original
// name for every function here (e.g. `function orderTotal(o){ return
// pricing.orderTotal(PRODUCTS, o); }`), so every existing call site
// (`orderTotal(o)`, `getOrderItems(o)`, ...) keeps working unchanged — see
// REFACTOR_SECURITY_REPORT.md, Phase 2 section, for the full rationale.
import { norm } from '../utils/text.js';
import { iid } from '../utils/ids.js';

export function productByName(products, n) {
  if (!n) return null;
  const x = norm(n);
  return products.find(p => norm(p.name) === x) || null;
}

export function productForItem(products, item) {
  return products.find(p => String(p.id) === String(item && item.productId || '')) || productByName(products, item && item.product);
}

export function normalizeItem(products, item, o) {
  item = item || {};
  const product = item.product != null ? String(item.product) : String((o && o.product) || '');
  const p = products.find(x => String(x.id) === String(item.productId || '')) || productByName(products, product);
  return {
    id: item.id || iid(),
    productId: p ? p.id : (item.productId || ''),
    product: p ? p.name : product,
    variant: item.variant || ((o && o.variant) || 'Standard'),
    qty: Math.max(1, Number(item.qty != null ? item.qty : (o && o.qty)) || 1),
    unitPrice: Number(item.unitPrice != null ? item.unitPrice : (o && o.unitPrice)) || 0,
    unitCost: Number(item.unitCost != null ? item.unitCost : ((o && o.unitCost) != null ? o.unitCost : (p ? p.cost : 0))) || 0
  };
}

export function getOrderItems(products, o) {
  const src = Array.isArray(o && o.items) && o.items.length ? o.items : [{}];
  return src.map(item => normalizeItem(products, item, o));
}

export function ensureOrderItems(products, o) {
  if (!o) return [];
  o.items = getOrderItems(products, o);
  if (o.orderLabel == null) o.orderLabel = '';
  return o.items;
}

export function itemQty(products, o) {
  return getOrderItems(products, o).reduce((s, item) => s + Number(item.qty || 0), 0);
}

export function itemGross(products, o) {
  return getOrderItems(products, o).reduce((s, item) => s + Number(item.qty || 0) * Number(item.unitPrice || 0), 0);
}

export function itemCost(products, o) {
  return getOrderItems(products, o).reduce((s, item) => s + Number(item.qty || 0) * Number(item.unitCost || 0), 0);
}

// Pure — no product lookup needed, operates on an already-normalized item.
export function itemLine(item) {
  const variant = item.variant && item.variant !== 'Standard' ? (' - ' + item.variant) : '';
  return (item.product || 'Produit') + variant + (Number(item.qty || 1) > 1 ? (' x' + Number(item.qty || 1)) : '');
}

export function productsLabel(products, o, separator) {
  return getOrderItems(products, o).filter(item => item.product).map(itemLine).join(separator || ' + ') || (o && o.product) || '—';
}

export function orderLabelText(products, o) {
  const label = String((o && o.orderLabel) || '').trim();
  return label || productsLabel(products, o);
}

export function orderLabelWithItems(products, o, separator) {
  const label = String((o && o.orderLabel) || '').trim(), items = productsLabel(products, o, separator);
  return label ? (label + ' — ' + items) : items;
}

export function packSuggestions(products, orders) {
  return [...new Set([...products.filter(p => norm(p.name).includes('pack')).map(p => p.name), ...orders.map(o => String(o.orderLabel || '').trim()).filter(Boolean)])].sort();
}

export function syncLegacyFields(products, o) {
  const items = ensureOrderItems(products, o);
  const first = items[0] || normalizeItem(products, {}, o);
  const qty = items.reduce((s, item) => s + Number(item.qty || 0), 0) || 1;
  o.product = items.map(item => item.product).filter(Boolean).join(' + ');
  o.variant = items.length === 1 ? first.variant : 'Multiple';
  o.qty = qty;
  o.unitPrice = items.length === 1 ? first.unitPrice : itemGross(products, { items }) / qty;
  o.unitCost = items.length === 1 ? first.unitCost : itemCost(products, { items }) / qty;
  return o;
}

export function orderTotal(products, o) {
  return Math.max(0, itemGross(products, o) - Number(o.discount || 0));
}

// Pure — no product lookup needed.
export function deliveryCost(o) {
  return o && o.company === 'Moi-même' ? 0 : Number(o && o.deliveryFee || 0);
}

export function unitCostOf(products, o) {
  return itemCost(products, o);
}

// `packagingCost` is a pre-computed value, not a callback (unlike wallet.js's
// monthStats/profitForRange) — the original called packagingCostForOrder()
// fresh each time, but that function's only real work here is arithmetic
// over already-known settings; its ensureInventorySettings() side effect
// still runs, just once in the caller (public/index.html's wrapper) instead
// of implicitly inside this pure function.
export function packagingCostForOrder(packagingItems, costPackaging) {
  const configured = (packagingItems || []).reduce((sum, item) => sum + Number(item.cost || 0) * Math.max(0, Number(item.usagePerOrder || 0)), 0);
  return configured > 0 ? configured : Number(costPackaging || 0);
}

export function orderMargin(products, o, packagingCost) {
  const noLiv = ['annulee', 'nouvelle', 'incomplete', 'a_confirmer'].includes(o.status);
  return orderTotal(products, o) - itemCost(products, o) - packagingCost - (noLiv ? 0 : deliveryCost(o));
}
